// ─── A company's sector, remembered ──────────────────────────────────────────
// The sector of a company changes when the company changes what it does —
// which is to say, almost never. Asking Finnhub for it on every single launch,
// once per holding, 150ms apart, spent the free tier's budget and the user's
// launch time on an answer that was the same yesterday.
//
// So it is stored per device for THIRTY DAYS. The number is not a guess about
// how fast the world moves: it is how long we are willing to be wrong about a
// field that a company changes once in its life, balanced against never
// picking up a correction at all.
//
// This is a cache, not a source. It only ever holds an answer that came back
// from the live lookup — nothing is invented here, nothing is defaulted, and a
// miss simply means the lookup runs. That is the difference between this and
// the hand-written sector tables that were removed (TICKER_SECTORS' successor
// SECTOR_LOOKUP, DECISIONS.md 2026-09-19): those asserted a sector nobody had
// asked for, this one only repeats what was already answered.
//
// Per device, deliberately: a shared table would need a migration and a write
// path for something that costs one lookup to rebuild.

import { industryToSector, SECTOR_UNKNOWN } from './sectorClassify';
import { companyName } from './companyName';

const KEY = 'si_sector_cache';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    const o = raw ? JSON.parse(raw) : null;
    return o && typeof o === 'object' ? o : {};
  } catch { return {}; }
}

function writeAll(map) {
  try { localStorage.setItem(KEY, JSON.stringify(map)); } catch { /* private mode: no cache, still correct */ }
}

// { sector, industry, name } — or null when unknown or stale.
export function getCachedSector(symbol) {
  if (!symbol) return null;
  const hit = readAll()[symbol];
  if (!hit || !hit.at || Date.now() - hit.at > TTL_MS) return null;
  if (!hit.sector) return null;
  // The sector is read again from the stored industry string, so a fix to the
  // mapping reaches a cached holding now, not 30 days later (sprint 16, 4.2).
  // No industry (an ETF) keeps what was stored.
  const sector = hit.industry ? (industryToSector(hit.industry) || SECTOR_UNKNOWN) : hit.sector;
  return { sector, industry: hit.industry ?? null, name: hit.name ? companyName(hit.name) : null };
}

// Only a real answer is stored. A failed lookup returns a null sector, and
// caching that would turn one bad request into thirty days of silence.
export function setCachedSector(symbol, { sector, industry = null, name = null } = {}) {
  if (!symbol || !sector) return;
  const map = readAll();
  map[symbol] = { sector, industry, name, at: Date.now() };
  // The store follows the portfolio, which is small; this bound is only here
  // so a long history of sold holdings cannot grow without limit.
  const keys = Object.keys(map);
  if (keys.length > 200) {
    keys.sort((a, b) => (map[a].at || 0) - (map[b].at || 0));
    for (const k of keys.slice(0, keys.length - 200)) delete map[k];
  }
  writeAll(map);
}
